import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageDropzone, FileUploadButton } from 'react-file-utils';
import { Tooltip } from '@material-ui/core';
import InsertPhotoIcon from '@material-ui/icons/InsertPhoto';
import InsertEmoticonIcon from '@material-ui/icons/InsertEmoticon';
import * as Sentry from '@sentry/browser';
import {
  ChatAutoComplete,
  useMessageInputContext,
  SendButton,
  useChannelStateContext,
} from 'stream-chat-react';

import { useCurrentUser, useNotify } from 'hooks';

import EmojiPicker from './EmojiPicker';
import UploadsPreview from './UploadsPreview';

const ACCEPTED_FILES = ['image/*'];

const MessageInputCustom = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const messageInput = useMessageInputContext();
  const channelContext = useChannelStateContext();
  const notify = useNotify();

  function handleUpload(files: FileList) {
    const fileList = Array.from(files);
    let error = false;

    for (const file of fileList) {
      const isImageType = file.type.includes('image/');
      const isImageExtension = /.(jpg|jpeg|png|gif)$/i.exec(file.name);

      if (!isImageType || !isImageExtension) {
        notify.error({ message: t('errors:imagesOnly') });
        error = true;
      }
    }

    if (error) {
      Sentry.captureMessage('An attempt was made to upload a non-image file', {
        user: {
          name: currentUser.full_name,
          email: currentUser.email_address,
          client_id: currentUser.client_id,
        },
      });
    }

    if (!error) {
      messageInput.uploadNewFiles(files);
    }
  }

  return (
    <div className="str-chat__input-flat">
      <ImageDropzone
        accept={channelContext.acceptedFiles}
        multiple={channelContext.multipleUploads}
        disabled={
          channelContext.maxNumberOfFiles !== undefined &&
          messageInput.numberOfUploads >= channelContext.maxNumberOfFiles
        }
        handleFiles={messageInput.uploadNewFiles}
      >
        <div className="str-chat__input-flat-wrapper">
          <EmojiPicker />
          <div className="str-chat__input-flat--textarea-wrapper">
            <UploadsPreview {...messageInput} />
            <ChatAutoComplete
              handleSubmit={messageInput.handleSubmit}
              onChange={messageInput.handleChange}
              value={messageInput.text}
              rows={1}
              placeholder={t('discussions:typeMessage')}
              onPaste={messageInput.onPaste}
            />
            <Tooltip title={t('discussions:openEmojiPicker')}>
              <span
                className="str-chat__input-flat-emojiselect"
                onClick={messageInput.openEmojiPicker}
              >
                <InsertEmoticonIcon />
              </span>
            </Tooltip>
            <FileUploadButton
              multiple={channelContext.multipleUploads}
              disabled={
                channelContext.maxNumberOfFiles !== undefined &&
                messageInput.numberOfUploads >= channelContext.maxNumberOfFiles
              }
              accepts={ACCEPTED_FILES}
              handleFiles={handleUpload}
            >
              <Tooltip title={t('discussions:attachFiles')}>
                <InsertPhotoIcon />
              </Tooltip>
            </FileUploadButton>
          </div>
          <SendButton sendMessage={messageInput.handleSubmit} />
        </div>
      </ImageDropzone>
    </div>
  );
};

export default memo(MessageInputCustom);
